module CEK
  ( Environment(..)
  , EnvEntry
  , genEnvironment
  , Closure(..)
  , genClosure
  , Kontinuation(..)
  , Argument(..)
  , Application(..)
  , Abstraction(..)
  , genKontinuation
  , checkStepBoundVariable
  , Query(..)
  , component
  ) where

import Prelude hiding (div)

import Control.Lazy (class Lazy, defer, fix)
import Control.Monad.Error.Class (class MonadThrow, throwError)
import Control.Monad.Gen (class MonadGen, oneOf, resize, sized, unfoldable)
import Control.Monad.RWS.Trans (lift)
import Control.Monad.Rec.Class (class MonadRec)
import Data.Array (cons, findMap, null, (:))
import Data.Array.NonEmpty (cons')
import Data.DateTime.Instant (diff)
import Data.Either (Either(..), either)
import Data.Generic.Rep (class Generic)
import Data.Int (quot, toNumber, trunc)
import Data.List (List(..))
import Data.Maybe (Maybe(..), maybe)
import Data.Number (sqrt)
import Data.Show.Generic (genericShow)
import Data.String.Gen (genAlphaString)
import Data.Time.Duration (Milliseconds(..))
import Effect.Aff (Aff)
import Effect.Now (now)
import Expr (Expr)
import Expr as Expr
import Halogen
  ( ClassName(..)
  , Component
  , HalogenM
  , liftEffect
  , mkComponent
  , mkEval
  , put
  )
import Halogen.HTML
  ( HTML
  , button
  , div
  , li_
  , ol
  , table
  , tbody_
  , td
  , td_
  , text
  , th_
  , thead_
  , tr
  , tr_
  )
import Halogen.HTML.Events (onClick)
import Halogen.HTML.Properties (class_, disabled)

type Control = Expr

type Environment = Array EnvEntry
type EnvEntry = { name :: String, value :: Closure }
newtype Closure = MkClosure { target :: Expr, environment :: Environment }

derive instance Eq Closure
derive instance Generic Closure _

instance Show Closure where
  show c = genericShow c

genE ::
  forall g.
  Applicative g =>
  MonadGen g =>
  MonadRec g =>
  g Closure ->
  g Environment
genE genClos = unfoldable genEntry
  where
  genEntry = ado
    name <- genAlphaString
    value <- genClos
    in { name, value }

genC ::
  forall g.
  Applicative g =>
  MonadRec g =>
  MonadGen g =>
  g Expr ->
  g Environment ->
  g Closure
genC genExpr genEnv = ado
  target <- genExpr
  environment <- genEnv
  in MkClosure { target, environment }

pred :: Int -> Int
pred s = max 0 (s - 1)

genEnvironment ::
  forall g.
  Applicative g =>
  MonadRec g =>
  MonadGen g =>
  Lazy (g Environment) =>
  g Environment
genEnvironment = fix
  ( \ge -> genE $ genC Expr.generator $ resize
      (trunc <<< sqrt <<< toNumber <<< pred)
      ge
  )

genClosure ::
  forall g.
  Applicative g =>
  MonadRec g =>
  MonadGen g =>
  Lazy (g Closure) =>
  g Closure
genClosure = fix
  (\gc -> genC (resize half Expr.generator) $ genE $ resize half gc)
  where
  half n = quot n 2

data Kontinuation
  = Done
  | KontArg Argument
  | KontApp Application

type Argument =
  { argument :: Expr, environment :: Environment, next :: Kontinuation }

type Application =
  { abstraction :: Abstraction
  , environment :: Environment
  , next :: Kontinuation
  }

type Abstraction = { binder :: String, body :: Expr }

derive instance Eq Kontinuation

genKont ::
  forall g.
  MonadRec g =>
  MonadGen g =>
  g Expr ->
  g Environment ->
  g Kontinuation ->
  g Kontinuation
genKont expr env k = sized (\size -> if size <= 1 then small else large)
  where
  smaller = resize (max 0 <<< (_ - 1)) k
  small = pure Done
  large = oneOf $ cons' small [ KontArg <$> arg, KontApp <$> app ]
  arg = ado
    argument <- expr
    environment <- env
    next <- smaller
    in { argument, environment, next }
  app = ado
    abstraction <- ado
      binder <- genAlphaString
      body <- expr
      in { binder, body }
    environment <- env
    next <- smaller
    in { abstraction, environment, next }

genKontinuation ::
  forall g.
  MonadRec g =>
  MonadGen g =>
  Lazy (g Expr) =>
  Lazy (g Environment) =>
  Lazy (g Kontinuation) =>
  g Kontinuation
genKontinuation = fix $ genKont Expr.generator genEnvironment

type VariableNotFound = { name :: String, environment :: Environment }
data MachineError = VariableNotFound VariableNotFound

derive instance Eq MachineError

type MachineState =
  { control :: Control
  , environment :: Environment
  , kontinuation :: Kontinuation
  }

cekStep ::
  forall m.
  MonadThrow MachineError m =>
  Control ->
  Environment ->
  Kontinuation ->
  m (Either Expr MachineState)
cekStep (Expr.Variable x) e k =
  maybe (throwError (VariableNotFound { name: x, environment: e }))
    (pure <<< Right <<< onClosure)
    (findMap onEntry e)
  where
  onClosure (MkClosure closure) =
    { control: closure.target, environment: closure.environment, kontinuation: k }
  onEntry ent = if x == ent.name then Just ent.value else Nothing
cekStep (Expr.Application { function: control, argument }) e k =
  pure
    ( Right
        { control
        , environment: e
        , kontinuation: KontArg { argument, environment: e, next: k }
        }
    )
cekStep r _ Done = pure (Left r)
cekStep (Expr.Abstraction { binder, body }) e (KontArg arg) =
  pure
    ( Right
        { control: arg.argument
        , environment: arg.environment
        , kontinuation: KontApp
            { abstraction: { binder, body }, environment: e, next: arg.next }
        }
    )
cekStep abs e (KontApp app) =
  pure
    ( Right
        { control: app.abstraction.body
        , environment: cons
            { name: app.abstraction.binder
            , value: MkClosure { target: abs, environment: e }
            }
            app.environment
        , kontinuation: app.next
        }
    )

displayEnvironment :: forall slot action. Environment -> HTML slot action
displayEnvironment env =
  if null env then
    div [ class_ $ ClassName "environment-empty" ] [ text "<empty>" ]
  else
    table
      [ class_ $ ClassName "environment-nonEmpty" ]
      [ thead_ [ tr_ [ th_ [ text "Name" ], th_ [ text "Value" ] ] ]
      , tbody_ (displayEntry <$> env)
      ]
  where
  displayEntry ent =
    tr
      [ class_ $ ClassName "environment-entry" ]
      [ td_ [ text ent.name ]
      , td
          [ class_ $ ClassName "environment-closure" ]
          $ displayClosure ent.value
      ]
  displayClosure (MkClosure clo) =
    [ Expr.render clo.target, displayEnvironment clo.environment ]

displayKontinuation :: forall slot action. Kontinuation -> HTML slot action
displayKontinuation kont =
  ol [ class_ $ ClassName "kontinuation" ] $ kontArray kont
  where
  andThen = text ", and then..."
  kontArray Done = [ li_ [ text "evaluation is complete." ] ]
  kontArray (KontArg arg) =
    li_
      [ text "evaluate the argument"
      , Expr.render arg.argument
      , text "in the environment"
      , displayEnvironment arg.environment
      , text "and apply the current result to it"
      , andThen
      ]
      : kontArray arg.next
  kontArray (KontApp app) =
    li_
      [ text "evaluate the function body"
      , Expr.render app.abstraction.body
      , text "in the envionrment"
      , displayEnvironment app.environment
      , text
          ( "extended by binding the current result to "
              <> show app.abstraction.binder
          )
      , andThen
      ]
      : kontArray app.next

--| Assert CEK behavior on a bound variable: x, E, K -> t, E', K where closure(t, E') = E[x]
checkStepBoundVariable ::
  String -> Closure -> Environment -> Kontinuation -> Boolean
checkStepBoundVariable x c@(MkClosure clo) et k =
  Right (Right ost) == cekStep (Expr.Variable x) e k
  where
  e :: Environment
  e = cons { name: x, value: c } et

  ost :: MachineState
  ost = { control: clo.target, environment: clo.environment, kontinuation: k }

-- | Assert CEK behavior on a free variable: VariableNotFound
-- cekVarF :: Gen String -> Gen Encironment -> Gen Kontinuation -> Boolean

-- | Assert CEK behavior on an application: (f e), E, K -> f, E, Arg(e, E, K)
-- cekApp :: Gen Expr -> Gen Expr -> Gen Environment -> Gen Kontinuation -> Boolean

-- | Assert CEK behavior on an abstraction in function position: Abs, E, Arg(t, E', K) -> t, E', App(Abs, E, K)
-- cekAbsFun :: Gen String -> Gen Expr -> Gen Envrinonment -> Gen Kontinuation -> Boolean

-- | Assert CEK behavior on an abstraction in an argument position:
-- | Abs, E, App(\x.Exp, E', K) -> Exp, E'[x = closure(Abs, E)], K
-- cekAbsArg :: Gen String -> Gen Expr -> Gen String -> Gen Expr -> Gen Environment -> Gen Kontinuation -> Boolean

initialMachineState :: Expr -> MachineState
initialMachineState control = { control, environment: [], kontinuation: Done }

displayMachineState :: forall slot action. MachineState -> HTML slot action
displayMachineState st =
  table
    [ class_ $ ClassName "machine-state" ]
    [ thead_
        [ tr_
            [ th_ [ text "Control" ]
            , th_ [ text "Environment" ]
            , th_ [ text "Kontinuation" ]
            ]
        ]
    , tbody_
        [ tr_
            [ td_ [ Expr.render st.control ]
            , td_ [ displayEnvironment st.environment ]
            , td_ [ displayKontinuation st.kontinuation ]
            ]
        ]
    ]

newtype Query :: Type -> Type
newtype Query a = Init Expr

type ListZipper a =
  { previousReverse :: List a, current :: a, future :: List a }

lzInit :: forall a. ListZipper a -> ListZipper a
lzInit lz = case lz.previousReverse of
  Nil -> lz
  Cons current previousReverse ->
    { previousReverse, current, future: Cons lz.current lz.future }

lzHead :: forall a. ListZipper a -> ListZipper a
lzHead lz = case lz.previousReverse of
  Nil -> lz
  _ -> lzHead $ lzInit lz

lzTail :: forall a. ListZipper a -> ListZipper a
lzTail lz = case lz.future of
  Nil -> lz
  Cons current future ->
    { previousReverse: Cons lz.current lz.previousReverse, current, future }

type Snoc a = { initReverse :: List a, last :: a }

zipperSnoc :: forall a. Snoc a -> ListZipper a
zipperSnoc { initReverse, last } =
  { previousReverse: initReverse, current: last, future: Nil }

snocLast :: forall a. ListZipper a -> Snoc a
snocLast lz = case lz.future of
  Nil -> { initReverse: lz.previousReverse, last: lz.current }
  _ -> snocLast $ lzTail lz

type FinalState =
  { process :: Snoc MachineState
  , result :: Either MachineError Expr
  }

type ReviewState =
  { process :: ListZipper MachineState, result :: Either MachineError Expr }

data ComponentState
  = Uninitialized
  | Working (ListZipper MachineState)
  | Final FinalState
  | Review ReviewState

data UIControl
  = MoveHead
  | JumpBack
  | StepBack
  | StepForward
  | JumpForward
  | RunForward
  | MoveLast

derive instance Generic UIControl _
derive instance Eq UIControl

instance Show UIControl where
  show = genericShow

controlState :: ComponentState -> UIControl -> Maybe (Aff ComponentState)
controlState = case _ of
  Uninitialized -> const Nothing
  Working lz -> case _ of
    MoveHead -> pwork <$> moveHead lz
    JumpBack -> pwork <$> jumpBack lz
    StepBack -> pwork <$> stepBack lz
    StepForward ->
      Just $ either (growUntil (const $ pure true)) pwork $ stepForward lz
    JumpForward ->
      Just $ either (growUntil (pure <<< newKont)) pwork $ jumpForward lz
      where
      newKont ms = lz.current.kontinuation /= ms.kontinuation
    RunForward -> Just $ defer \_ -> do
      start <- liftEffect now
      let done = liftEffect $ timeUp start
      loop done lz >>= either (growUntil (const done)) pwork
      where
      timeUp start = do
        current <- now
        pure $ Milliseconds 100.0 <= diff current start
      loop isDone = l
        where
        l llz = do
          done <- isDone
          if done then pure nlz else either (pure <<< Left) l nlz
          where
          nlz = stepForward llz
    MoveLast -> Nothing
    where
    pwork x = defer \_ -> pure $ Working x
    growUntil isDone x = defer \_ -> loop x
      where
      loop process = case process.last of
        last@{ control, environment, kontinuation } ->
          case cekStep control environment kontinuation of
            Left err -> pure $ Final { process, result: Left err }
            Right step -> case step of
              Left done -> pure $ Final { process, result: Right done }
              Right current -> do
                cond <- isDone current
                if cond then
                  pure
                    $ Working
                        { previousReverse: initReverse
                        , current
                        , future: Nil
                        }
                else loop { initReverse, last: current }
                where
                initReverse = Cons last process.initReverse
  Final fs -> case _ of
    MoveHead -> justReview $ reviewLast { process = lzHead reviewLast.process }
    JumpBack -> back
    StepBack -> back
    StepForward -> Nothing
    JumpForward -> Nothing
    RunForward -> Nothing
    MoveLast -> Nothing
    where
    reviewLast =
      { process: zipperSnoc fs.process
      , result: fs.result
      }
    justReview x = Just $ defer \_ -> pure $ Review x
    back = justReview reviewLast
  Review rs -> case _ of
    MoveHead -> mkReview <$> moveHead rs.process
    JumpBack -> mkReview <$> jumpBack rs.process
    StepBack -> mkReview <$> stepBack rs.process
    StepForward -> forward $ stepForward rs.process
    JumpForward -> forward $ jumpForward rs.process
    RunForward -> moveLast
    MoveLast -> moveLast
    where
    mkReview lz = pure $ Review $ rs { process = lz }
    mkFinal process = pure $ Final { process, result: rs.result }
    forward = Just <<< either mkFinal mkReview
    moveLast =
      Just $ defer \_ ->
        pure $ Final { process: snocLast rs.process, result: rs.result }
  where
  moveHead lz = case lz.previousReverse of
    Nil -> Nothing
    Cons current previousReverse ->
      Just
        $ lzHead { previousReverse, current, future: Cons lz.current lz.future }
  stepBack lz = case lz.previousReverse of
    Nil -> Nothing
    Cons current previousReverse ->
      Just { previousReverse, current, future: Cons lz.current lz.future }
  jumpBack lz = loop lz.current.kontinuation <$> stepBack lz
    where
    loop k = l
      where
      l i = if k == i.current.kontinuation then maybe i l $ stepBack i else i
  stepForward lz = case lz.future of
    Nil -> Left { initReverse: lz.previousReverse, last: lz.current }
    Cons current future ->
      Right
        { previousReverse: Cons lz.current lz.previousReverse, current, future }
  jumpForward lz = stepForward lz >>= loop lz.current.kontinuation
    where
    loop k = l
      where
      l i =
        if k == i.current.kontinuation then stepForward i >>= l
        else Right i

component :: forall output. Component Query Unit output Aff
component = mkComponent
  { initialState: const Uninitialized
  , render
  , eval: mkEval
      { initialize: Nothing
      , receive: const Nothing
      , handleAction: (_ >>= put) <<< lift
      , handleQuery
      , finalize: Nothing
      }
  }
  where
  render st = case st of
    Uninitialized ->
      div
        [ class_ $ ClassName "cek-component-uninitialized" ]
        [ text "No valid expression, yet." ]
    Working lz ->
      div
        [ class_ $ ClassName "cek-component-working" ]
        [ controls, displayMachineState $ lz.current ]
    Final fs ->
      div
        [ class_ $ ClassName "cek-component-final" ]
        [ controls, displayResult $ fs.result ]
      where
      displayResult = case _ of
        Left err -> case err of
          VariableNotFound vnf ->
            div
              [ class_ $ ClassName "cek-machine-error-variable-not-found" ]
              [ text "The variable "
              , text $ show vnf.name
              , text " was not found in the environemnt:"
              , displayEnvironment vnf.environment
              ]
        Right value -> Expr.render value
    Review rs ->
      div
        [ class_ $ ClassName "cek-component-review" ]
        [ controls, displayMachineState $ rs.process.current ]
    where
    controlProp = maybe (disabled true) (onClick <<< const) <<< controlState st
    controls =
      div
        [ class_ $ ClassName "cek-component-controls" ]
        [ button [ controlProp MoveHead ] [ text "|< First" ]
        , button [ controlProp JumpBack ] [ text "<< Kont Back" ]
        , button [ controlProp StepBack ] [ text "< Step Back" ]
        , button [ controlProp StepForward ] [ text "Step Forward >" ]
        , button [ controlProp JumpForward ] [ text "Kont Forward >>" ]
        , button [ controlProp RunForward ] [ text "100ms Forward >>>" ]
        , button [ controlProp MoveLast ] [ text "Final >|" ]
        ]

  handleQuery ::
    forall a action slots.
    Query a ->
    HalogenM ComponentState action slots output Aff (Maybe a)
  handleQuery (Init expr) = do
    put
      $ Working
          { previousReverse: Nil
          , current: initialMachineState expr
          , future: Nil
          }
    pure Nothing
